import type { MRT_Row, MRT_TableInstance, MRT_TableState } from '@talentmesh/table';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import canSelectCandidate from '../../Utils/CanSelectCandidate';
import { CandidatesOverviewContextType } from '../CandidatesOverviewContext/CandidatesOverviewContext';
import useColumns from './utils/useColumns';
import useManualSorting from './utils/useManualSorting';
import { useShadcnTable } from '@/Hooks';
import { ApplicantRecruitmentOverviewResponse, CandidateTabs } from '@/Models/CandidateOverview';

type CandidatesTableState = Partial<MRT_TableState<ApplicantRecruitmentOverviewResponse>>;
type LocalStorageTableState = { tableVersion?: number } & CandidatesTableState;
type MRTStateMap = Map<string, LocalStorageTableState>;

// increment when table state is changed, e.g. a new column was added or default visibility changed
const currentTableVersion = 2;
const mrtStateKey = 'mrtState';

export const CandidatesTableContext = createContext<
    MRT_TableInstance<ApplicantRecruitmentOverviewResponse> | undefined
>(undefined);

export const useCandidatesTable = ({
    assessmentId,
    candidates,
    totalCandidatesCount,
    loading: isLoading,
    tabValue,
    testTypes,
    includesExperienceCriteria,
    sort,
}: CandidatesOverviewContextType) => {
    const columns = useColumns(testTypes, includesExperienceCriteria);
    const { onSortingChange, sorting } = useManualSorting(sort);
    const enableRowSelection = useCallback(
        (row: MRT_Row<ApplicantRecruitmentOverviewResponse>) =>
            tabValue === CandidateTabs.Open && canSelectCandidate(row.original),
        [isLoading],
    );

    const initialState = useMemo<CandidatesTableState>(() => {
        const mrtStateStr = localStorage.getItem(mrtStateKey);
        const defaultState: Partial<CandidatesTableState> = {
            columnPinning: { left: ['mrt-row-select', 'isFavorite', 'applicant', 'applicantAction'] },
            columnVisibility: {
                phoneNumber: false,
                candidateEmail: false,
                noticePeriod: false,
                validWorkPermit: false,
                willingToRelocate: false,
                salaryExpectation: false,
                education: false,
                invitedAt: false,
                startedAt: false,
                completedAt: false,
                invitedForInterviewAt: false,
                interviewingAt: false,
                hiredAt: false,
                rejectedAt: false,
            },
        };

        if (mrtStateStr) {
            const mrtStateMap: MRTStateMap = new Map(JSON.parse(mrtStateStr));
            const tableState = mrtStateMap.get(assessmentId);

            if (tableState?.tableVersion === currentTableVersion) {
                return tableState;
            } else {
                // delete stale version of table customization for this recruitment
                mrtStateMap.delete(assessmentId);
                localStorage.setItem(mrtStateKey, JSON.stringify(Array.from(mrtStateMap.entries())));
            }
        }

        return defaultState;
    }, []);

    const table = useShadcnTable({
        columns,
        data: candidates,
        state: { showSkeletons: isLoading, isLoading: false, sorting },
        initialState,
        enableRowSelection,
        onSortingChange,
        rowCount: totalCandidatesCount,
        mantineSelectAllCheckboxProps: { disabled: tabValue !== CandidateTabs.Open },
    });

    useEffect(() => {
        return () => {
            let mrtStateMap: MRTStateMap = new Map();
            const mrtStateStr = localStorage.getItem(mrtStateKey);

            if (mrtStateStr) {
                mrtStateMap = new Map(JSON.parse(mrtStateStr));
            }

            // rowSelection only lives on the page, and shall not be persisted across
            const tableState: LocalStorageTableState = {
                ...table.getState(),
                rowSelection: undefined,
                tableVersion: currentTableVersion,
            };
            mrtStateMap.set(assessmentId, tableState);

            localStorage.setItem(mrtStateKey, JSON.stringify(Array.from(mrtStateMap.entries())));
        };
    }, []);

    return table;
};

export const useCandidatesTableContext = () => {
    const context = useContext(CandidatesTableContext);
    if (!context) {
        throw new Error('useCandidatesTableContext must be used within its provider');
    }
    return context;
};
